import { Check, Edit02, Loading01, XClose } from "@untitledui/icons";
import type { ReactElement, ReactNode } from "react";
import { cx } from "@/utils/cx";
import {
	type DisplayFieldFeedback,
	type DisplayFieldIcon,
	type DisplayFieldState,
	displayFieldTrailingIconToneClass,
	DisplayFieldFeedback as Feedback,
	DisplayFieldState as FieldState,
	shouldShowEditAffordance,
} from "./display-field.shared";

export interface DisplayFieldShellProps {
	label: string;
	state: DisplayFieldState;
	isPopulated: boolean;
	isMultiline?: boolean;
	leadingIcon?: DisplayFieldIcon;
	showLeadingIcon?: boolean;
	hintText?: string;
	showHintText?: boolean;
	feedback?: DisplayFieldFeedback;
	/** Show edit pencil. Defaults to true when not disabled / active / saving. */
	showEdit?: boolean;
	onEdit?: () => void;
	editAriaLabel?: string;
	/**
	 * Shown only while the field is in edit mode. Clears the draft and does not save.
	 */
	onClear?: () => void;
	clearAriaLabel?: string;
	trailing?: ReactNode;
	/**
	 * When true, the shell is nested inside a pressable MenuTrigger / button.
	 * Renders the edit affordance as non-interactive chrome (no nested buttons).
	 */
	isTriggerChild?: boolean;
	/**
	 * Set false when a parent draws the underline around extra content.
	 */
	showUnderline?: boolean;
	children: ReactNode;
	className?: string;
	"data-testid"?: string;
}

/**
 * Shared underline container for DisplayInput / DisplaySelect / DisplayTextarea.
 */
export function DisplayFieldShell({
	label,
	state,
	isPopulated,
	isMultiline = false,
	leadingIcon: LeadingIcon,
	showLeadingIcon,
	hintText,
	showHintText,
	feedback = Feedback.Idle,
	showEdit = true,
	onEdit,
	editAriaLabel,
	onClear,
	clearAriaLabel,
	trailing,
	isTriggerChild = false,
	showUnderline = true,
	children,
	className,
	"data-testid": dataTestId,
}: DisplayFieldShellProps): ReactElement {
	const isDisabled = state === FieldState.Disabled;
	const isError = state === FieldState.Error;
	const isActive = state === FieldState.Active;
	const isHovered = state === FieldState.Hovered;
	const showLabelAbove = isPopulated || isActive;
	const shouldShowLeading = Boolean(LeadingIcon) && (showLeadingIcon ?? true);
	const shouldShowHint = showHintText ?? (isError && Boolean(hintText));
	const renderEdit = shouldShowEditAffordance({
		state,
		feedback,
		isEditable: showEdit,
	});
	/** Figma Hovered states use cursor-pointer on the whole field. */
	const isFieldInteractive =
		!isTriggerChild &&
		renderEdit &&
		Boolean(onEdit) &&
		!isDisabled &&
		!isActive;
	const showEditChrome =
		renderEdit &&
		feedback === Feedback.Idle &&
		(isTriggerChild || Boolean(onEdit));
	const trailingIconTone = displayFieldTrailingIconToneClass({
		isDisabled,
		isEmphasized: isHovered || isActive,
	});

	const labelAndValue = (
		<>
			{showLabelAbove ? (
				<p className="w-full truncate font-display text-xs font-extrabold tracking-[0.24px] text-quaternary">
					{label}
				</p>
			) : null}
			{showLabelAbove ? children : null}
			{!showLabelAbove ? (
				<p className="w-full truncate text-md font-semibold text-quaternary">
					{label}
				</p>
			) : null}
		</>
	);

	const contentClassName = cx(
		"flex min-w-0 flex-1 flex-col justify-center overflow-hidden",
		isMultiline && "items-start",
	);

	return (
		<div
			className={cx("relative flex w-full flex-col gap-1.5", className)}
			data-testid={dataTestId}
			data-state={state}
		>
			<div
				className={cx(
					"group relative flex w-full gap-2 px-4 py-2",
					showUnderline && "border-b-2",
					isMultiline
						? "min-h-[100px] items-start"
						: "h-[54px] min-h-11 items-center",
					showUnderline &&
						(isError
							? "border-fg-error-primary"
							: isActive
								? "border-fg-brand-primary"
								: "border-border-secondary"),
					!isDisabled &&
						(isHovered || isActive
							? "bg-primary_hover"
							: "hover:bg-primary_hover"),
					isDisabled && "cursor-not-allowed opacity-80",
					(isFieldInteractive || isTriggerChild) &&
						!isDisabled &&
						!isActive &&
						"cursor-pointer",
				)}
			>
				{shouldShowLeading && LeadingIcon ? (
					<div
						className={cx(
							"flex shrink-0 items-center",
							isMultiline && "pt-0.5",
						)}
					>
						<LeadingIcon className="size-6 text-fg-quaternary" />
					</div>
				) : null}

				{isFieldInteractive ? (
					<button
						type="button"
						className={cx(
							contentClassName,
							"cursor-pointer border-0 bg-transparent p-0 text-left",
						)}
						aria-label={editAriaLabel ?? `Edit ${label}`}
						onClick={onEdit}
					>
						{labelAndValue}
					</button>
				) : (
					<div className={contentClassName}>{labelAndValue}</div>
				)}

				<div
					className={cx(
						"flex shrink-0 items-center justify-center gap-0.5",
						isMultiline && "items-start self-stretch",
					)}
				>
					{/* Figma FeedbackIndicator ΓÇö Saving (5228:89418) / Saved (5228:89757) / End */}
					{feedback === Feedback.Saving ? (
						<span
							className="flex items-center gap-1 text-xs font-semibold text-quaternary"
							aria-live="polite"
						>
							<Loading01 className="size-3.5 animate-spin text-fg-quaternary" />
							Saving
						</span>
					) : null}
					{feedback === Feedback.Saved || feedback === Feedback.End ? (
						<span
							className={cx(
								"flex items-center gap-1 text-xs font-semibold text-success-primary transition-opacity duration-300",
								feedback === Feedback.End && "opacity-0",
							)}
							aria-live="polite"
						>
							<Check className="size-3.5 text-success-primary" />
							Saved
						</span>
					) : null}
					{isActive && onClear ? (
						<button
							type="button"
							className={cx(
								"flex size-[21px] cursor-pointer items-center justify-center outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
								trailingIconTone,
							)}
							aria-label={clearAriaLabel ?? `Clear ${label}`}
							data-testid={
								dataTestId ? `${dataTestId}-clear` : "display-field-clear"
							}
							onMouseDown={(event) => {
								event.preventDefault();
							}}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								onClear();
							}}
						>
							<XClose className="size-5" />
						</button>
					) : null}
					{trailing}
					{showEditChrome ? (
						isTriggerChild ? (
							<span
								className={cx(
									"flex size-[21px] items-center justify-center",
									trailingIconTone,
								)}
								aria-hidden
							>
								<Edit02 className="size-5" />
							</span>
						) : (
							<button
								type="button"
								className={cx(
									"flex size-[21px] cursor-pointer items-center justify-center outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed",
									trailingIconTone,
								)}
								aria-label={editAriaLabel ?? `Edit ${label}`}
								aria-hidden={isFieldInteractive || undefined}
								tabIndex={isFieldInteractive ? -1 : undefined}
								data-testid={
									dataTestId ? `${dataTestId}-edit` : undefined
								}
								disabled={isDisabled}
								onClick={onEdit}
							>
								<Edit02 className="size-5" />
							</button>
						)
					) : null}
				</div>
			</div>

			{shouldShowHint && hintText ? (
				<p
					className={cx(
						"text-xs font-medium",
						isError ? "text-error-primary" : "text-secondary",
					)}
					id={dataTestId ? `${dataTestId}-hint` : undefined}
				>
					{hintText}
				</p>
			) : null}
		</div>
	);
}
